import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useIntl } from 'react-intl'

import { useApiClient } from '@/shared/api/use-api-client'
import { loadMore, type CursorPage } from '@/shared/api/paging'
import { ChooserDialog, type ChooserItem } from '@/shared/search'
import { TextField } from '@/shared/ui'

import { enabledRolesPath, parseRoleSummary, type RoleSummary } from './roles-api'

/** A role picked in the chooser. */
export interface ChosenRole {
  /** Such as `rol_2y1VdX9p7k3FZ8mH6nQaRs`. */
  readonly id: string
  readonly name: string
}

interface RoleChooserBase {
  readonly open: boolean
  readonly onClose: () => void
  /** "Choose Role", or "Add Roles" with `multi`, when omitted. */
  readonly title?: string
}

export type RoleChooserProps = RoleChooserBase &
  (
    | { readonly multi?: false; readonly onConfirm: (role: ChosenRole) => void }
    | { readonly multi: true; readonly onConfirm: (roles: readonly ChosenRole[]) => void }
  )

interface Search {
  readonly path: string
  readonly page: CursorPage<RoleSummary> | null
  readonly loading: boolean
  readonly failed: boolean
}

const notSearched: Search = { path: '', page: null, loading: false, failed: false }

/**
 * The role chooser every role picker uses (AC-27): search enabled roles by name, pick one, or
 * several with `multi`, then OK hands them back. It lists only the roles the caller may see,
 * which for anyone without `roles` read is the roles they may assign.
 */
export function RoleChooser(props: RoleChooserProps) {
  const intl = useIntl()
  const client = useApiClient()
  const [name, setName] = useState('')
  const [search, setSearch] = useState<Search>(notSearched)
  const [picked, setPicked] = useState<readonly ChosenRole[]>([])
  const abort = useRef<AbortController | null>(null)
  const multi = props.multi === true
  const { onClose, open } = props

  useEffect(() => () => abort.current?.abort(), [])

  const run = useCallback(
    async (path: string, previous: CursorPage<RoleSummary> | null) => {
      abort.current?.abort()
      const controller = new AbortController()
      abort.current = controller
      setSearch({ path, page: previous, loading: true, failed: false })
      try {
        const page = await loadMore(client, path, previous, parseRoleSummary, { signal: controller.signal })
        // A newer search or a reset aborted this one: only the current request updates the chooser.
        if (!controller.signal.aborted) {
          setSearch({ path, page, loading: false, failed: false })
        }
      } catch {
        // The client has logged it.
        if (!controller.signal.aborted) {
          setSearch({ path, page: previous, loading: false, failed: true })
        }
      }
    },
    [client],
  )

  const runSearch = useCallback(() => void run(enabledRolesPath(name), null), [run, name])
  const runMore = useCallback(() => void run(search.path, search.page), [run, search.path, search.page])

  const typeName = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), [])

  const toggle = useCallback(
    (item: ChooserItem) => {
      const role = { id: item.id, name: item.label }
      setPicked((current) =>
        !multi
          ? [role]
          : current.some((chosen) => chosen.id === role.id)
            ? current.filter((chosen) => chosen.id !== role.id)
            : [...current, role],
      )
    },
    [multi],
  )

  // Each opening starts afresh, however the dialog was closed.
  const reset = useCallback(() => {
    abort.current?.abort()
    setName('')
    setSearch(notSearched)
    setPicked([])
  }, [])

  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect -- resets the dialog as it opens
      reset()
    }
  }, [open, reset])

  const close = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const confirm = useCallback(() => {
    if (props.multi === true) {
      props.onConfirm(picked)
    } else if (picked[0] !== undefined) {
      props.onConfirm(picked[0])
    }
    reset()
  }, [props, picked, reset])

  const results = useMemo<readonly ChooserItem[] | null>(
    () => (search.page === null ? null : search.page.data.map((role) => ({ id: role.id, label: role.name }))),
    [search.page],
  )
  const pickedIds = useMemo(() => picked.map((role) => role.id), [picked])
  const title =
    props.title ?? intl.formatMessage({ id: multi ? 'accessControl.roleChooser.addTitle' : 'accessControl.roleChooser.chooseTitle' })
  const filters = (
    <TextField label={intl.formatMessage({ id: 'accessControl.roleChooser.name' })} value={name} onChange={typeName} />
  )
  const shared = {
    open: props.open,
    onClose: close,
    title,
    filters,
    onSearch: runSearch,
    results,
    loading: search.loading,
    failed: search.failed,
    hasMore: search.page?.pagination.hasMore ?? false,
    onLoadMore: runMore,
    onToggle: toggle,
    onConfirm: confirm,
  }

  return multi ? (
    <ChooserDialog {...shared} multi selected={pickedIds} />
  ) : (
    <ChooserDialog {...shared} selected={picked[0]?.id ?? null} />
  )
}
